import { useId } from 'react';

/**
 * The truck from above, as a chassis drawing.
 *
 * Four elevations were four pictures of the same lorry, and none of them showed
 * where anything was: a side view cannot say which of six rear wheels, and the
 * front view shows nothing under the cab. A plan view shows the frame rails,
 * every axle, every wheel position and the running gear hung between them — the
 * things an inspection is actually about.
 *
 * Drawn, not photographed: every component is its own <P> group with a key, so
 * a mark, a damage code and a photograph attach to that part and no other.
 *
 * Left and right are the DRIVER'S left and right, with the cab at the top of
 * the drawing — the same convention as the workshop reference, so nobody has to
 * translate.
 */

export type PartState = 'ok' | 'attention' | 'fail' | 'na';
export type AxleCount = 2 | 3;
export interface TruckPart { key: string; label: string }

export const DAMAGE_CODES = [
  { code: 'C',  label: 'Cut' },     { code: 'H',  label: 'Hole' },
  { code: 'D',  label: 'Dent' },    { code: 'BR', label: 'Broken' },
  { code: 'M',  label: 'Missing' }, { code: 'S',  label: 'Scratch' },
  { code: 'P',  label: 'Patched' },
] as const;
export type DamageCode = typeof DAMAGE_CODES[number]['code'];

export type BodyType =
  | 'tanker' | 'tipper' | 'flatbed' | 'box' | 'mixer' | 'pickup' | 'bus' | 'other';

export const BODY_LABEL: Record<BodyType, string> = {
  tanker: 'Water tanker', tipper: 'Dumper / tipper', flatbed: 'Flatbed',
  box: 'Box body', mixer: 'Mixer', pickup: 'Pickup', bus: 'Bus', other: 'Other',
};

/**
 * How many axles each body usually has, so picking a body type sets a sensible
 * default instead of making someone answer a question the shape already implies.
 * A pickup is never a 3-axle truck; a water tanker in this fleet always is.
 */
export const DEFAULT_AXLES: Record<BodyType, AxleCount> = {
  tanker: 3, tipper: 3, mixer: 3, flatbed: 3, box: 2, pickup: 2, bus: 2, other: 3,
};

const FILL: Record<PartState, string> = {
  ok:        'fill-muted stroke-border',
  attention: 'fill-amber-500/30 stroke-amber-500',
  fail:      'fill-rose-500/30 stroke-rose-500',
  na:        'fill-muted/40 stroke-border',
};
const NEXT: Record<PartState, PartState> = {
  ok: 'attention', attention: 'fail', fail: 'ok', na: 'attention',
};

/**
 * Every markable part, in the order you would walk around the truck.
 *
 * Wheel keys carry their axle and side, so "axle 2, right outer" is a distinct
 * thing from "axle 2, right inner" — which is the whole reason for a plan view.
 */
export function truckParts(axles: AxleCount): TruckPart[] {
  const parts: TruckPart[] = [
    { key: 'bumper.front',   label: 'Front bumper' },
    { key: 'bumper.icc',     label: 'Rear (ICC) bumper' },
    { key: 'cab',            label: 'Cab' },
    { key: 'frame.left',     label: 'Chassis frame rail — left' },
    { key: 'frame.right',    label: 'Chassis frame rail — right' },
    { key: 'engine',         label: 'Engine' },
    { key: 'transmission',   label: 'Transmission' },
    { key: 'propshaft',      label: 'Propeller shaft' },
    { key: 'steering',       label: 'Steering box and drag link' },
    { key: 'suspension.front', label: 'Front (steer) suspension' },
    { key: 'tank.left',      label: 'Fuel tank — left' },
    { key: 'tank.right',     label: 'Fuel tank — right' },
    { key: 'air.reservoirs', label: 'Air reservoirs' },
    { key: 'exhaust',        label: 'Exhaust and muffler' },
    { key: 'body',           label: 'Body' },
  ];
  // Steer axle, then one drive axle per remaining axle.
  parts.push(
    { key: 'axle.steer',        label: 'Steer axle' },
    { key: 'wheel.steer.left',  label: 'Wheel — steer, left' },
    { key: 'wheel.steer.right', label: 'Wheel — steer, right' },
  );
  if (axles === 2) {
    // Rigid: the first rear axle is the driven one, the second trails behind
    // it. Both carry duals, so the back of a rigid is eight tyres.
    for (let a = 1; a <= 2; a++) {
      const at = a === 1 ? 'drive' : 'rear';
      parts.push(
        { key: `axle.${a}`, label: a === 1 ? 'Drive axle' : 'Rear axle' },
        { key: `diff.${a}`, label: `Differential — ${at} axle` },
        { key: `wheel.${a}.left.out`,  label: `Wheel — ${at}, left outer` },
        { key: `wheel.${a}.left.in`,   label: `Wheel — ${at}, left inner` },
        { key: `wheel.${a}.right.in`,  label: `Wheel — ${at}, right inner` },
        { key: `wheel.${a}.right.out`, label: `Wheel — ${at}, right outer` },
      );
    }
  } else {
    // Tractor and semi-trailer: duals on the drive axle only.
    parts.push(
      { key: 'axle.drive',            label: 'Drive axle' },
      { key: 'diff.drive',            label: 'Differential — drive axle' },
      { key: 'wheel.drive.left.out',  label: 'Wheel — drive, left outer' },
      { key: 'wheel.drive.left.in',   label: 'Wheel — drive, left inner' },
      { key: 'wheel.drive.right.in',  label: 'Wheel — drive, right inner' },
      { key: 'wheel.drive.right.out', label: 'Wheel — drive, right outer' },
      { key: 'coupling',              label: 'Fifth wheel / coupling' },
    );
    for (let a = 1; a <= 3; a++) {
      parts.push(
        { key: `axle.${a}`,        label: `Trailer axle ${a}` },
        { key: `wheel.${a}.left`,  label: `Wheel — trailer ${a}, left` },
        { key: `wheel.${a}.right`, label: `Wheel — trailer ${a}, right` },
      );
    }
  }
  parts.push({ key: 'spare', label: 'Spare wheel' });
  return parts;
}

export function TruckDiagram({
  states, onToggle, codes = {}, onCode, axles = 3, bodyType = 'other', readOnly = false,
  onSelect, photoCounts = {},
}: {
  states: Record<string, PartState>;
  onToggle: (key: string, next: PartState) => void;
  onSelect?: (key: string) => void;
  photoCounts?: Record<string, number>;
  codes?: Record<string, DamageCode>;
  onCode?: (key: string, code: DamageCode | null) => void;
  axles?: AxleCount;
  bodyType?: BodyType;
  readOnly?: boolean;
}) {
  const uid = useId();
  const parts = truckParts(axles);
  const st = (k: string): PartState => states[k] ?? 'ok';
  const labelOf = (k: string) => parts.find((p) => p.key === k)?.label ?? k;

  const P = ({ k, children }: { k: string; children: React.ReactNode }) => {
    const state = st(k);
    return (
      <g
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        aria-label={`${labelOf(k)}: ${state}`}
        className={`${FILL[state]} ${readOnly ? '' : 'cursor-pointer hover:opacity-75'} transition`}
        strokeWidth={1.6}
        onClick={readOnly ? undefined : () => (onSelect ? onSelect(k) : onToggle(k, NEXT[state]))}
        onKeyDown={readOnly ? undefined : (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (onSelect) onSelect(k); else onToggle(k, NEXT[state]);
          }
        }}
      >
        <title>
          {labelOf(k)} — {state}{codes[k] ? ` (${codes[k]})` : ''}
          {photoCounts[k] ? ` · ${photoCounts[k]} photo(s)` : ''}
        </title>
        {children}
      </g>
    );
  };

  /* Layout, in drawing units. The truck runs left (front) to right (rear);
     y=100 is the centreline, the frame rails sit either side of it. */
  const NOSE = 40, TAIL = 940;
  const MID = 100, RAIL_T = 74, RAIL_B = 126;   // frame rails
  const TRACK_OUT = 26, TRACK_IN = 52;          // wheel centres from the rail

  /** Axle x positions: steer up front, drives grouped at the back. */
  /** Steer up front, then one drive axle per rear axle, grouped at the back. */
  const STEER_X = 110;
  const DRIVE_X = 250;
  const COUPLE_X = 300;
  /** The trailer's axles, grouped at its rear. */
  const rigid = axles === 2;
  /** Rigid: two dual axles at the tail. Artic: three single axles under the trailer. */
  const rearX = rigid
    ? [792, 856]
    : Array.from({ length: 3 }, (_, i) => 890 - (2 - i) * 70);

  /** Body shape changes what sits on the frame, per the reference. */
  const body = () => {
    const x = 330, w = 560;
    switch (bodyType) {
      case 'tanker':
        return <><rect x={x} y="44" width={w} height="112" rx="56" />
                 <line x1={x + w * 0.33} y1="44" x2={x + w * 0.33} y2="156" />
                 <line x1={x + w * 0.66} y1="44" x2={x + w * 0.66} y2="156" /></>;
      case 'tipper':
        return <><rect x={x} y="40" width={w} height="120" rx="6" />
                 <line x1={x + 20} y1="40" x2={x + 20} y2="160" /></>;
      case 'mixer':
        return <><ellipse cx={x + w / 2} cy="100" rx={w / 2} ry="58" /></>;
      case 'pickup':
        return <rect x={x} y="52" width={w * 0.55} height="96" rx="6" />;
      case 'bus':
        return <rect x={x - 120} y="38" width={w + 120} height="124" rx="14" />;
      case 'box':
        return <rect x={x} y="38" width={w} height="124" rx="4" />;
      case 'flatbed':
      default:
        return <><rect x={x} y="52" width={w} height="96" rx="3" />
                 {[0.25, 0.5, 0.75].map((f) => (
                   <line key={f} x1={x + w * f} y1="52" x2={x + w * f} y2="148" />
                 ))}</>;
    }
  };

  return (
    <div aria-labelledby={`${uid}-t`}>
      <span id={`${uid}-t`} className="sr-only">
        The truck from above, {axles} axles — press a part to mark it
      </span>

      <svg viewBox="0 0 980 200" className="w-full h-auto">
        {/* the ground the drawing sits on */}
        <rect x="0" y="0" width="980" height="200" className="fill-transparent" />

        {/* ---- chassis frame rails: the spine everything hangs from ---- */}
        <P k="frame.left">
          <rect x={NOSE + 30} y={RAIL_T - 5}
                width={(rigid ? TAIL - 40 : COUPLE_X) - NOSE - 20} height="10" />
        </P>
        <P k="frame.right">
          <rect x={NOSE + 30} y={RAIL_B - 5}
                width={(rigid ? TAIL - 40 : COUPLE_X) - NOSE - 20} height="10" />
        </P>
        {!rigid && (
          <>
            {/* the trailer's own rails, from the coupling to its tail */}
            <rect x={COUPLE_X + 10} y={RAIL_T - 4} width={TAIL - COUPLE_X - 40} height="8"
                  className="fill-muted stroke-border" strokeWidth={1.4} />
            <rect x={COUPLE_X + 10} y={RAIL_B - 4} width={TAIL - COUPLE_X - 40} height="8"
                  className="fill-muted stroke-border" strokeWidth={1.4} />
          </>
        )}

        {/* ---- bumpers ---- */}
        <P k="bumper.front"><rect x={NOSE} y="34" width="14" height="132" rx="7" /></P>
        <P k="bumper.icc"><rect x={TAIL - 6} y="52" width="12" height="96" rx="4" /></P>

        {/* ---- cab over the steer axle ---- */}
        <P k="cab"><rect x={NOSE + 20} y="34" width="150" height="132" rx="10" /></P>

        {/* ---- running gear down the centre ---- */}
        <P k="engine"><rect x={NOSE + 40} y="74" width="90" height="52" rx="4" /></P>
        <P k="transmission"><rect x={NOSE + 138} y="84" width="60" height="32" rx="4" /></P>
        <P k="propshaft"><rect x={NOSE + 150} y={MID - 4} width={DRIVE_X - NOSE - 160} height="8" rx="4" /></P>
        <P k="steering"><rect x={NOSE + 30} y="44" width="40" height="20" rx="3" /></P>
        <P k="exhaust"><rect x="176" y="106" width="70" height="18" rx="9" /></P>

        {/* ---- slung either side of the frame ---- */}
        <P k="tank.left"><rect x="180" y="48" width="90" height="22" rx="6" /></P>
        <P k="tank.right"><rect x="180" y="130" width="90" height="22" rx="6" /></P>
        <P k="air.reservoirs"><rect x="196" y="76" width="50" height="18" rx="9" /></P>

        {/* ---- the body on the frame ---- */}
        <P k="body">{body()}</P>

        {/* ---- axles and wheels ---- */}
        {/* the steer axle */}
        <g>
          <P k="axle.steer"><rect x={STEER_X - 5} y="26" width="10" height="148" rx="4" /></P>
          <P k="wheel.steer.left"><rect x={STEER_X - 22} y="22" width="44" height="30" rx="6" /></P>
          <P k="wheel.steer.right"><rect x={STEER_X - 22} y="148" width="44" height="30" rx="6" /></P>
          <P k="suspension.front"><rect x={STEER_X - 34} y={MID - 12} width="24" height="24" rx="3" /></P>
        </g>

        {!rigid && (
          <>
            {/* the tractor's drive axle: the only duals on an artic */}
            <g>
              <P k="axle.drive"><rect x={DRIVE_X - 5} y="26" width="10" height="148" rx="4" /></P>
              <P k="diff.drive"><circle cx={DRIVE_X} cy={MID} r="14" /></P>
              <P k="wheel.drive.left.out"><rect x={DRIVE_X - 22} y="18" width="44" height="22" rx="5" /></P>
              <P k="wheel.drive.left.in"><rect x={DRIVE_X - 22} y="44" width="44" height="22" rx="5" /></P>
              <P k="wheel.drive.right.in"><rect x={DRIVE_X - 22} y="134" width="44" height="22" rx="5" /></P>
              <P k="wheel.drive.right.out"><rect x={DRIVE_X - 22} y="160" width="44" height="22" rx="5" /></P>
            </g>
            <P k="coupling"><circle cx={COUPLE_X} cy={MID} r="15" /></P>
          </>
        )}

        {rearX.map((x, i) => {
          const a = i + 1;
          return (
            <g key={a}>
              <P k={`axle.${a}`}>
                <rect x={x - (rigid ? 6 : 4)} y={rigid ? 14 : 34} width={rigid ? 12 : 8}
                      height={rigid ? 172 : 132} rx="4" />
              </P>
              {rigid ? (
                <>
                  <P k={`diff.${a}`}><circle cx={x} cy={MID} r="14" /></P>
                  <P k={`wheel.${a}.left.out`}><rect x={x - 25} y="14" width="50" height="24" rx="5" /></P>
                  <P k={`wheel.${a}.left.in`}><rect x={x - 25} y="42" width="50" height="24" rx="5" /></P>
                  <P k={`wheel.${a}.right.in`}><rect x={x - 25} y="134" width="50" height="24" rx="5" /></P>
                  <P k={`wheel.${a}.right.out`}><rect x={x - 25} y="162" width="50" height="24" rx="5" /></P>
                </>
              ) : (
                <>
                  <P k={`wheel.${a}.left`}><rect x={x - 20} y="26" width="40" height="28" rx="6" /></P>
                  <P k={`wheel.${a}.right`}><rect x={x - 20} y="146" width="40" height="28" rx="6" /></P>
                </>
              )}
            </g>
          );
        })}

        {/* ---- spare, slung behind the last axle ---- */}
        <P k="spare"><rect x={COUPLE_X + 24} y={MID - 15} width="42" height="30" rx="6" /></P>

        {/* ---- orientation, so nobody guesses which side is which ---- */}
        <text x={NOSE + 4} y="14" className="fill-muted-foreground" fontSize="11">FRONT</text>
        <text x={TAIL - 46} y="14" className="fill-muted-foreground" fontSize="11">REAR</text>
        <text x="8" y={RAIL_T - 34} className="fill-muted-foreground" fontSize="11">LEFT</text>
        <text x="8" y={RAIL_B + 44} className="fill-muted-foreground" fontSize="11">RIGHT</text>
      </svg>

      <p className="text-[11px] text-muted-foreground mt-1">
        Seen from above, cab at the left. Left and right are the driver’s.
      </p>
    </div>
  );
}
